import { defineStore } from 'pinia';
import { ETL, EtlRepo } from '@fibl/zdb-common';

export const useETLStore = defineStore('etl', {
	state: () => ({
		currentJob: null as ETL.JobType | null,
		jobPromise: null as Promise<ETL.JobType> | null,
		jobLatest: null as ETL.JobType | null,
		startTime: 0,
		lastUpdate: 0,
		extracted: {
			added: 0,
			skipped: 0,
			created: 0,
			updated: 0,
			unloaded: 0,
			processed: 0,
			errors: [] as ETL.RowError[],
		},
	}),

	persist: {
		paths: ['currentJob', 'startTime'],
	},

	getters: {
		progress(): ETL.JobType {
			if (!this.jobLatest) return {} as ETL.JobType;
			return this.jobLatest;
		},
		elapsed(): number {
			return this.lastUpdate - this.startTime;
		},
		rate(): number {
			let p = this.jobLatest?.stats.processed || 0;
			let el = this.elapsed > 1000 ? this.elapsed / 1000 : 1;
			return p / el;
		},
		eta(): number {
			const rate = this.rate;
			if (rate <= 0) return 0;
			let rem = (this.jobLatest?.stats.added || 1) - (this.jobLatest?.stats.processed || 0);
			return rem / rate;
		},
	},

	actions: {
		async startJob() {
			this.currentJob = await EtlRepo.startJob({ job: this.currentJob!.id });
			return this.tryContinue()!;
		},
		async createJob(params: ETL.CreateJob) {
			if (params.config.length !== 36) {
				let tpl = await EtlRepo.getConfig({ alias: params.config });
				params.config = tpl.id;
			}
			this.resetJob();
			this.currentJob = await EtlRepo.createJob(params);
			this.jobLatest = this.currentJob;
		},

		tryContinue(jobId?: string) {
			if (!this.currentJob) return;
			this.startTime = Date.now();
			const started = this.startTime;
			this.jobPromise = EtlRepo.pollUntilJobComplete(
				jobId ? jobId : this.currentJob!.id,
				1000,
				1000 * 60 * 20,
				(update: ETL.JobType) => {
					if (started !== this.startTime || !this.currentJob) return;
					console.log(update);
					this.jobLatest = update;
					this.lastUpdate = Date.now();
					this.currentJob.stats = update.stats;
					this.extracted.added = update.stats.added;
					this.extracted.created = update.stats.created;
					this.extracted.errors = update.stats.errors;
					this.extracted.processed = update.stats.processed;
					this.extracted.skipped = update.stats.skipped;
					this.extracted.updated = update.stats.updated;
					this.extracted.unloaded = update.stats.unloaded;
				},
			);
			return this.jobPromise!;
		},

		resetJob() {
			this.currentJob = null;
			this.jobPromise = null;
			this.jobLatest = null;
			this.extracted = {
				added: 0,
				skipped: 0,
				created: 0,
				updated: 0,
				unloaded: 0,
				processed: 0,
				errors: [] as ETL.RowError[],
			};
		},

		async loadLastJob() {
			try {
				if (!this.currentJob?.id) return;
				let jobId = this.currentJob.id;
				let job = (await EtlRepo.getJob({ job: jobId })) as ETL.JobType;
				if (['running', 'queued'].includes(job!.status)) {
					this.tryContinue(job.id);
				}
			} catch (e) {
				console.log('cannot load last job', e);
			}
		},
	},
});
